/**
 * Redact the current text selection inside a <textarea>.
 *
 * Reads `el.selectionStart` / `el.selectionEnd`, replaces the highlighted
 * span with a run of X's the same length, then restores the caret at the
 * end of the redaction so a second click is uneventful (no accidental
 * double-redact). Returns the new value so the caller can pipe it into
 * their state setter.
 *
 *   const next = redactSelection(textareaRef.current, value);
 *   if (next != null) setValue(next);
 *
 * Returns null when there's nothing selected (caller should surface a
 * hint - "highlight the name first" - rather than mutate blindly).
 */
export function redactSelection(el, value) {
  if (!el) return null;
  const start = el.selectionStart ?? 0;
  const end = el.selectionEnd ?? 0;
  if (end <= start) return null;

  const selected = value.slice(start, end);
  // Trim leading/trailing whitespace inside the selection so a name
  // dragged with a stray space in front doesn't produce a leading gap.
  const leadMatch = selected.match(/^\s*/);
  const trailMatch = selected.match(/\s*$/);
  const lead = leadMatch ? leadMatch[0] : '';
  const trail = trailMatch ? trailMatch[0] : '';
  const core = selected.slice(lead.length, selected.length - trail.length);
  if (!core) return null;

  // The renderer only redacts runs of 4+ X's, so pad short selections up
  // to 4 so single-letter initials still whiteout.
  const runLen = Math.max(core.length, 4);
  const redacted = lead + 'X'.repeat(runLen) + trail;

  const next = value.slice(0, start) + redacted + value.slice(end);

  // Restore caret at the end of the new redaction on the next tick so
  // the DOM has re-rendered with the new value.
  const caret = start + redacted.length;
  requestAnimationFrame(() => {
    try {
      el.focus();
      el.setSelectionRange(caret, caret);
    } catch { /* noop */ }
  });

  return next;
}
